/**
 * Create an invoice for a booking when one is missing (attendance / event dashboard).
 * Totals follow My RSVP: stored ticket line prices and customPrice override, then event ticket prices,
 * plus guests the inviter pays for on the same invoice.
 */

import {
  addDoc,
  collection,
  doc,
  getDoc,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { Event, Invoice, RSVP } from '@/types';
import {
  calculateInvoiceAmounts,
  generateUnitInvoiceNumber,
  INVOICE_AMOUNT_EPS,
} from '@/lib/invoice-utils';
import { buildInvoicePaymentLink } from '@/lib/payment-links';
import { getInvoicePayerRsvp } from '@/lib/invoice-payer-rsvp';
import { isActiveInvoiceData, computeRsvpSubtotalForInvoice } from '@/lib/event-utils';
import { getCreditBookingFeePenceForEvent } from '@/lib/platform-booking-fee';
import { computeCreditRecoveryTotalPounds } from '@/lib/credit-booking-fee';

function coerceToDate(value: unknown): Date | null {
  if (value == null || value === '') {
    return null;
  }
  if (typeof (value as { toDate?: () => Date }).toDate === 'function') {
    const d = (value as { toDate: () => Date }).toDate();
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  // Firestore Timestamp-like plain objects ({ seconds, nanoseconds }) from some serializers
  if (
    typeof value === 'object' &&
    value !== null &&
    'seconds' in value &&
    typeof (value as { seconds: unknown }).seconds === 'number'
  ) {
    const seconds = (value as { seconds: number }).seconds;
    const nanos = (value as { nanoseconds?: number }).nanoseconds ?? 0;
    const parsed = new Date(seconds * 1000 + Math.floor(nanos / 1e6));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

function coerceToDateOrNow(value: unknown): Date {
  return coerceToDate(value) ?? new Date();
}

function mapPaymentHistoryEntry(raw: unknown): Invoice['paymentHistory'][number] | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  const payment = raw as Record<string, unknown>;
  const method = (payment.method as Invoice['paymentHistory'][number]['method']) || 'card';
  const date = coerceToDate(payment.date);
  const row: Invoice['paymentHistory'][number] = {
    amount: Number(payment.amount) || 0,
    // Prefer real timestamp; never invent "now" for missing payment dates.
    date: date ?? new Date(NaN),
    method,
    processingFee: Number(payment.processingFee) || 0,
  };
  if (payment.voided === true) {
    row.voided = true;
  }
  if (payment.voidedAt != null) {
    const voidedAt = coerceToDate(payment.voidedAt);
    if (voidedAt) {
      row.voidedAt = voidedAt;
    }
  }
  if (typeof payment.voidedByUid === 'string') {
    row.voidedByUid = payment.voidedByUid;
  }
  if (typeof payment.transactionId === 'string') {
    row.transactionId = payment.transactionId;
  }
  if (payment.subtotal !== undefined) {
    row.subtotal = Number(payment.subtotal) || 0;
  }
  if (payment.vatAmount !== undefined) {
    row.vatAmount = Number(payment.vatAmount) || 0;
  }
  if (payment.transactionFee !== undefined) {
    row.transactionFee = Number(payment.transactionFee) || 0;
  }
  if (payment.accountingSplit && typeof payment.accountingSplit === 'object') {
    const split = payment.accountingSplit as { lodgeTransfer?: unknown; platformProfit?: unknown };
    row.accountingSplit = {
      lodgeTransfer: Number(split.lodgeTransfer) || 0,
      platformProfit: Number(split.platformProfit) || 0,
    };
  }
  if (typeof payment.stripePaymentIntentId === 'string') {
    row.stripePaymentIntentId = payment.stripePaymentIntentId;
  }
  if (typeof payment.stripeChargeId === 'string') {
    row.stripeChargeId = payment.stripeChargeId;
  }
  if (typeof payment.sumupCheckoutId === 'string') {
    row.sumupCheckoutId = payment.sumupCheckoutId;
  }
  if (typeof payment.sumupTransactionCode === 'string') {
    row.sumupTransactionCode = payment.sumupTransactionCode;
  }
  if (typeof payment.slickpayInvoiceId === 'string') {
    row.slickpayInvoiceId = payment.slickpayInvoiceId;
  }
  return row;
}

function mapInvoiceDoc(id: string, d: Record<string, unknown>): Invoice {
  const createdAt = coerceToDateOrNow(d.createdAt);
  const updatedAt = coerceToDateOrNow(d.updatedAt);

  const rawBreakdown = d.allInPriceBreakdown;
  let allInPriceBreakdown: Invoice['allInPriceBreakdown'];
  if (
    rawBreakdown &&
    typeof rawBreakdown === 'object' &&
    !Array.isArray(rawBreakdown)
  ) {
    const b = rawBreakdown as { ticketPrice?: unknown; creditCostRecovery?: unknown };
    allInPriceBreakdown = {
      ticketPrice: Number(b.ticketPrice) || 0,
      creditCostRecovery: Number(b.creditCostRecovery) || 0,
    };
  }

  const paymentHistory = (Array.isArray(d.paymentHistory) ? d.paymentHistory : [])
    .map(mapPaymentHistoryEntry)
    .filter((p): p is Invoice['paymentHistory'][number] => p != null);

  const remindersSent = (Array.isArray(d.remindersSent) ? d.remindersSent : [])
    .map((reminder) => coerceToDate(reminder))
    .filter((d): d is Date => d != null);

  return {
    id,
    eventId: d.eventId as string,
    unitId: d.unitId as string,
    ownerUid: (d.ownerUid as string) ?? '',
    totalAmount: Number(d.totalAmount) || 0,
    amountPaid: Number(d.amountPaid) || 0,
    outstandingBalance: Number(d.outstandingBalance) || 0,
    status: d.status as Invoice['status'],
    rsvps: (d.rsvps as string[]) || [],
    paymentHistory,
    paymentLink: (d.paymentLink as string) || '',
    remindersSent,
    createdAt,
    updatedAt,
    invoiceNumber: d.invoiceNumber as string | undefined,
    accountingTotals: d.accountingTotals as Invoice['accountingTotals'],
    subtotal: d.subtotal !== undefined ? Number(d.subtotal) : undefined,
    vatAmount: d.vatAmount !== undefined ? Number(d.vatAmount) : undefined,
    transactionFee: d.transactionFee !== undefined ? Number(d.transactionFee) : undefined,
    ...(allInPriceBreakdown ? { allInPriceBreakdown } : {}),
    pendingVerificationAmount:
      d.pendingVerificationAmount !== undefined ? Number(d.pendingVerificationAmount) : undefined,
  };
}

export interface HostInvoiceRecalcInput {
  event: Event;
  payer: RSVP;
  allRsvps: RSVP[];
  amountPaid?: number;
  pendingVerificationAmount?: number;
  previousStatus?: Invoice['status'];
  transactionFee?: number;
}

export interface HostInvoiceRecalcResult {
  totalAmount: number;
  subtotal: number;
  outstandingBalance: number;
  status: Invoice['status'];
  rsvpIds: string[];
  accountingTotals: {
    totalLodgeTransfer: number;
    totalPlatformProfit: number;
    platformFeeTotal: number;
  };
  allInPriceBreakdown?: {
    ticketPrice: number;
    creditCostRecovery: number;
  };
  transactionFee?: number;
}

function deriveInvoiceStatusAfterRecalc(
  totalAmount: number,
  amountPaid: number,
  pendingVerification: number,
  previousStatus: Invoice['status'] | undefined,
): Invoice['status'] {
  const reserved = amountPaid + pendingVerification;
  const outstanding = totalAmount - reserved;
  if (outstanding <= INVOICE_AMOUNT_EPS) {
    // Only verified payments settle an invoice; a claimed transfer awaiting verification doesn't.
    if (pendingVerification > INVOICE_AMOUNT_EPS && totalAmount - amountPaid > INVOICE_AMOUNT_EPS) {
      return 'pending_verification';
    }
    return 'paid';
  }
  if (previousStatus === 'pending_verification' && pendingVerification > INVOICE_AMOUNT_EPS) {
    return 'pending_verification';
  }
  if (amountPaid > INVOICE_AMOUNT_EPS) {
    return 'partial';
  }
  return 'outstanding';
}

/** Resolve the host RSVP from an `invitedBy` value (user id or host rsvp id). */
export function resolveHostRsvpByInvitedBy(
  invitedBy: string | undefined | null,
  allRsvps: RSVP[],
): RSVP | undefined {
  const key = typeof invitedBy === 'string' ? invitedBy.trim() : '';
  if (!key) {
    return undefined;
  }
  return allRsvps.find((r) => {
    const uid = typeof r.userId === 'string' ? r.userId.trim() : '';
    return uid === key || r.id === key;
  });
}

export function hostInvoiceRecalcFirestorePatch(recalc: HostInvoiceRecalcResult): Record<string, unknown> {
  return {
    totalAmount: recalc.totalAmount,
    subtotal: recalc.subtotal,
    outstandingBalance: recalc.outstandingBalance,
    status: recalc.status,
    rsvps: recalc.rsvpIds,
    accountingTotals: recalc.accountingTotals,
    ...(recalc.allInPriceBreakdown ? { allInPriceBreakdown: recalc.allInPriceBreakdown } : {}),
    ...(recalc.transactionFee !== undefined ? { transactionFee: recalc.transactionFee } : {}),
    updatedAt: Timestamp.now(),
  };
}

/** Align RSVP `invoiceId` fields with canonical ids on a host invoice (clears stale links). */
export async function syncRsvpInvoiceLinksForHostInvoice(options: {
  unitId: string;
  eventId: string;
  invoiceId: string;
  canonicalRsvpIds: string[];
  allRsvps: RSVP[];
}): Promise<void> {
  const { unitId, eventId, invoiceId, canonicalRsvpIds, allRsvps } = options;
  const canonical = new Set(canonicalRsvpIds);
  const writes: Promise<void>[] = [];
  for (const r of allRsvps) {
    const linked = typeof r.invoiceId === 'string' && r.invoiceId.trim() === invoiceId;
    const shouldLink = canonical.has(r.id);
    if (shouldLink && !linked) {
      writes.push(
        updateDoc(doc(db, 'units', unitId, 'events', eventId, 'rsvps', r.id), {
          invoiceId,
          updatedAt: Timestamp.now(),
        }),
      );
    } else if (!shouldLink && linked) {
      writes.push(
        updateDoc(doc(db, 'units', unitId, 'events', eventId, 'rsvps', r.id), {
          invoiceId: '',
          updatedAt: Timestamp.now(),
        }),
      );
    }
  }
  if (writes.length > 0) {
    await Promise.all(writes);
  }
}

/** Persist recalculated host invoice totals and sync RSVP invoice links. */
export async function persistHostInvoiceRecalc(options: {
  unitId: string;
  eventId: string;
  invoice: Invoice;
  event: Event;
  payer: RSVP;
  allRsvps: RSVP[];
  transactionFee?: number;
}): Promise<HostInvoiceRecalcResult> {
  const { unitId, eventId, invoice, event, payer, allRsvps } = options;
  const transactionFee =
    options.transactionFee ??
    (typeof invoice.transactionFee === 'number' ? invoice.transactionFee : undefined) ??
    (await loadBankTransferTransactionFee());
  const recalc = computeHostInvoiceRecalc({
    event,
    payer,
    allRsvps,
    amountPaid: invoice.amountPaid || 0,
    pendingVerificationAmount: invoice.pendingVerificationAmount,
    previousStatus: invoice.status,
    transactionFee,
  });
  await updateDoc(
    doc(db, 'units', unitId, 'events', eventId, 'invoices', invoice.id),
    hostInvoiceRecalcFirestorePatch(recalc),
  );
  await syncRsvpInvoiceLinksForHostInvoice({
    unitId,
    eventId,
    invoiceId: invoice.id,
    canonicalRsvpIds: recalc.rsvpIds,
    allRsvps,
  });
  return recalc;
}

/**
 * Recalculate host invoice(s) after a guest's `invitedBy` or `inviterPaysFor` changes.
 * Returns Firestore-updated invoice snapshots for UI state.
 */
export async function recalcHostInvoicesAfterGuestPaymentEdit(options: {
  unitId: string;
  eventId: string;
  event: Event;
  previousGuest: RSVP;
  updatedGuest: RSVP;
  allRsvps: RSVP[];
  resolveInvoice: (invoiceId: string) => Invoice | null | undefined;
}): Promise<Invoice[]> {
  const { unitId, eventId, event, previousGuest, updatedGuest, allRsvps, resolveInvoice } = options;
  const mergedRsvps = allRsvps.map((r) => (r.id === updatedGuest.id ? updatedGuest : r));

  const hosts = new Map<string, RSVP>();
  const oldHost = resolveHostRsvpByInvitedBy(previousGuest.invitedBy, mergedRsvps);
  const newHost = resolveHostRsvpByInvitedBy(updatedGuest.invitedBy, mergedRsvps);
  if (oldHost) {
    hosts.set(oldHost.id, oldHost);
  }
  if (newHost) {
    hosts.set(newHost.id, newHost);
  }

  const updatedInvoices: Invoice[] = [];
  for (const host of hosts.values()) {
    const invoiceId = typeof host.invoiceId === 'string' ? host.invoiceId.trim() : '';
    if (!invoiceId) {
      continue;
    }
    const invoice = resolveInvoice(invoiceId);
    if (!invoice) {
      continue;
    }
    const recalc = await persistHostInvoiceRecalc({
      unitId,
      eventId,
      invoice,
      event,
      payer: host,
      allRsvps: mergedRsvps,
    });
    updatedInvoices.push({
      ...invoice,
      totalAmount: recalc.totalAmount,
      subtotal: recalc.subtotal,
      outstandingBalance: recalc.outstandingBalance,
      status: recalc.status,
      rsvps: recalc.rsvpIds,
      accountingTotals: recalc.accountingTotals,
      ...(recalc.allInPriceBreakdown ? { allInPriceBreakdown: recalc.allInPriceBreakdown } : {}),
      ...(recalc.transactionFee !== undefined ? { transactionFee: recalc.transactionFee } : {}),
      updatedAt: new Date(),
    });
  }

  const guestOnHostBill =
    !!newHost &&
    typeof updatedGuest.invitedBy === 'string' &&
    updatedGuest.invitedBy.trim() !== '' &&
    updatedGuest.inviterPaysFor !== false;
  const guestInvoiceId =
    guestOnHostBill && typeof newHost.invoiceId === 'string' ? newHost.invoiceId.trim() : '';

  await updateDoc(doc(db, 'units', unitId, 'events', eventId, 'rsvps', updatedGuest.id), {
    invoiceId: guestInvoiceId,
    ...(guestOnHostBill ? {} : { paid: false }),
    updatedAt: Timestamp.now(),
  });

  return updatedInvoices;
}

/** Recompute host invoice totals from current RSVPs (tickets + booking fees). */
export function computeHostInvoiceRecalc(
  input: HostInvoiceRecalcInput,
): HostInvoiceRecalcResult {
  const { event, payer, allRsvps } = input;
  const creditBookingFeePence = getCreditBookingFeePenceForEvent(event);
  const baseAmount = computeInvoiceBaseAmountForPayer(payer, event, allRsvps);
  const rsvpIds = getCanonicalInvoiceRsvpIds(payer, allRsvps);
  const billableRecoverySlots = countHostInvoiceCreditRecoverySlots(payer, event, allRsvps);
  const creditRecoveryTotal = computeCreditRecoveryTotalPounds(
    event,
    baseAmount,
    billableRecoverySlots,
    creditBookingFeePence,
  );
  const transactionFee = input.transactionFee ?? 0;
  const totalBaseWithRecovery = baseAmount + creditRecoveryTotal;
  const invoiceCalculation = calculateInvoiceAmounts(totalBaseWithRecovery, transactionFee);
  const amountPaid = Number(input.amountPaid) || 0;
  const pendingVerification =
    input.previousStatus === 'pending_verification'
      ? Number(input.pendingVerificationAmount) || 0
      : 0;
  const outstandingBalance = Math.max(
    0,
    Math.round((invoiceCalculation.totalAmount - amountPaid - pendingVerification) * 100) / 100,
  );
  const status = deriveInvoiceStatusAfterRecalc(
    invoiceCalculation.totalAmount,
    amountPaid,
    pendingVerification,
    input.previousStatus,
  );

  return {
    totalAmount: invoiceCalculation.totalAmount,
    subtotal: invoiceCalculation.subtotal,
    outstandingBalance,
    status,
    rsvpIds,
    accountingTotals: {
      totalLodgeTransfer: baseAmount,
      totalPlatformProfit: 0,
      platformFeeTotal: creditRecoveryTotal,
    },
    ...(creditRecoveryTotal > 0
      ? {
          allInPriceBreakdown: {
            ticketPrice: baseAmount,
            creditCostRecovery: creditRecoveryTotal,
          },
        }
      : {}),
    ...(transactionFee > 0 ? { transactionFee } : {}),
  };
}

/** Guests on this host's bill (inviter pays). */
function listGuestsInviterPaysFor(host: RSVP, allRsvps: RSVP[]): RSVP[] {
  const hostUid = typeof host.userId === 'string' ? host.userId.trim() : '';
  const hostId = host.id;
  return allRsvps.filter((g) => {
    if (g.apologies || g.id === host.id || g.status === 'cancelled' || g.status === 'declined') {
      return false;
    }
    const by = typeof g.invitedBy === 'string' ? g.invitedBy.trim() : '';
    if (!by) {
      return false;
    }
    const invitedByHost = by === hostUid || by === hostId;
    if (!invitedByHost) {
      return false;
    }
    return g.inviterPaysFor !== false;
  });
}

/** True when this RSVP has their own invoice (pays own fare, not on host invoice). */
export function isSelfPayingInvitee(rsvp: RSVP): boolean {
  const invitedBy = typeof rsvp.invitedBy === 'string' ? rsvp.invitedBy.trim() : '';
  return invitedBy.length > 0 && rsvp.inviterPaysFor === false;
}

export { computeRsvpSubtotalForInvoice } from '@/lib/event-utils';

/**
 * Ticket subtotal for one RSVP using only current event ticket prices.
 * Matches MyRSVPDashboardPage calculateRsvpCost: skips lines where the ticket
 * type is no longer present in the event config; no customPrice, stored line.price,
 * or costPerPerson fallback. Use for headline display figures so the personal
 * dashboard stays in parity with the RSVP dashboard.
 */
export function computeRsvpTicketSubtotalCurrentPrices(rsvp: RSVP, event: Event): number {
  if (!event.tickets?.length || !Array.isArray(rsvp.responses?.Tickets)) return 0;
  let sum = 0;
  for (const line of rsvp.responses.Tickets as { id?: string; qty?: number }[]) {
    const qty = Number(line?.qty || 0);
    if (!qty || !line?.id) continue;
    const ticketType = event.tickets.find((t) => t.id === line.id);
    if (!ticketType) continue;
    sum += qty * Number(ticketType.price || 0);
  }
  return sum;
}

/** Base ticket / pricing total for the payer RSVP and any guests on the same invoice. */
export function computeInvoiceBaseAmountForPayer(payer: RSVP, event: Event, allRsvps: RSVP[]): number {
  if (payer.apologies || payer.status === 'cancelled') {
    return 0;
  }
  if (isSelfPayingInvitee(payer)) {
    return computeRsvpSubtotalForInvoice(payer, event);
  }
  const guests = listGuestsInviterPaysFor(payer, allRsvps);
  const main = computeRsvpSubtotalForInvoice(payer, event);
  const guestSum = guests.reduce((s, g) => s + computeRsvpSubtotalForInvoice(g, event), 0);
  return main + guestSum;
}

/** RSVP ids that belong on this payer's invoice (host + inviter-paid guests, or sole self-payer). */
export function getCanonicalInvoiceRsvpIds(payer: RSVP, allRsvps: RSVP[]): string[] {
  if (isSelfPayingInvitee(payer)) {
    return [payer.id];
  }
  const guests = listGuestsInviterPaysFor(payer, allRsvps);
  return [payer.id, ...guests.map((g) => g.id)];
}

/**
 * Positive-ticket RSVP lines the payer covers on a host (or sole) invoice; used for credit recovery count.
 * Aligns with {@link countBookingFeeBillableSlots} when `invoice.rsvps` lists the same people in order.
 */
export function countHostInvoiceCreditRecoverySlots(payer: RSVP, event: Event, allRsvps: RSVP[]): number {
  if (payer.apologies || payer.status === 'cancelled') {
    return 0;
  }
  if (isSelfPayingInvitee(payer)) {
    return computeRsvpSubtotalForInvoice(payer, event) > 0 ? 1 : 0;
  }
  let n = computeRsvpSubtotalForInvoice(payer, event) > 0 ? 1 : 0;
  for (const g of listGuestsInviterPaysFor(payer, allRsvps)) {
    if (computeRsvpSubtotalForInvoice(g, event) > 0) {
      n++;
    }
  }
  return n;
}

async function loadBankTransferTransactionFee(): Promise<number> {
  try {
    const settingsDoc = await getDoc(doc(db, 'systemSettings', 'platform'));
    if (!settingsDoc.exists()) {
      return 0;
    }
    const data = settingsDoc.data() as {
      paymentMethods?: { bankTransfer?: { transactionFee?: number } };
    };
    return data.paymentMethods?.bankTransfer?.transactionFee ?? 0;
  } catch {
    return 0;
  }
}

export type EnsureInvoiceResult =
  | { ok: true; invoice: Invoice; created: boolean }
  | { ok: false; message: string };

/**
 * If the payer already has an invoice id, loads it from Firestore.
 * Otherwise creates a new invoice document and links all relevant RSVPs.
 */
export async function ensureInvoiceForBooking(options: {
  event: Event;
  rsvps: RSVP[];
  viewerRsvp: RSVP;
  ownerUid?: string;
}): Promise<EnsureInvoiceResult> {
  const { event, rsvps, viewerRsvp, ownerUid } = options;
  const unitId = event.unitId;
  const eventId = event.id;
  if (!unitId || !eventId) {
    return { ok: false, message: 'Event is missing unit or id.' };
  }

  const payer = getInvoicePayerRsvp(viewerRsvp, rsvps);
  const existingId = typeof payer.invoiceId === 'string' ? payer.invoiceId.trim() : '';

  if (existingId) {
    const snap = await getDoc(doc(db, 'units', unitId, 'events', eventId, 'invoices', existingId));
    if (snap.exists()) {
      const data = snap.data();
      if (data && isActiveInvoiceData(data)) {
        return { ok: true, invoice: mapInvoiceDoc(snap.id, data as Record<string, unknown>), created: false };
      }
    }
  }

  const transactionFee = await loadBankTransferTransactionFee();
  const creditBookingFeePence = getCreditBookingFeePenceForEvent(event);
  const baseAmount = computeInvoiceBaseAmountForPayer(payer, event, rsvps);
  
  // Credit recovery fee (booking fee on guest invoice; amount from event settings)
  const rsvpIds = getCanonicalInvoiceRsvpIds(payer, rsvps);
  const billableRecoverySlots = countHostInvoiceCreditRecoverySlots(payer, event, rsvps);
  const creditRecoveryTotal = computeCreditRecoveryTotalPounds(
    event,
    baseAmount,
    billableRecoverySlots,
    creditBookingFeePence,
  );
  
  const totalBaseWithRecovery = baseAmount + creditRecoveryTotal;
  const invoiceCalculation = calculateInvoiceAmounts(totalBaseWithRecovery, transactionFee);
  const invoiceNumber = await generateUnitInvoiceNumber(db, unitId);

  const owner =
    typeof payer.userId === 'string' && payer.userId.trim() !== '' ? payer.userId.trim() : ownerUid ?? '';

  const newInvoiceData: Record<string, unknown> = {
    eventId,
    unitId,
    ownerUid: owner,
    totalAmount: invoiceCalculation.totalAmount,
    subtotal: invoiceCalculation.subtotal,
    vatAmount: 0,
    vatRate: 0,
    ...(transactionFee > 0 ? { transactionFee } : {}),
    amountPaid: 0,
    outstandingBalance: invoiceCalculation.totalAmount,
    status: 'outstanding',
    rsvps: rsvpIds,
    paymentHistory: [],
    paymentLink: '',
    remindersSent: [],
    invoiceNumber,
    accountingTotals: {
      totalLodgeTransfer: baseAmount,
      totalPlatformProfit: 0,
      platformFeeTotal: creditRecoveryTotal,
    },
    allInPriceBreakdown: creditRecoveryTotal > 0 ? {
      ticketPrice: baseAmount,
      creditCostRecovery: creditRecoveryTotal,
    } : null,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  };

  try {
    const invoiceRef = await addDoc(
      collection(db, 'units', unitId, 'events', eventId, 'invoices'),
      newInvoiceData,
    );
    const invoiceId = invoiceRef.id;
    const paymentLink = buildInvoicePaymentLink(
      typeof window !== 'undefined' ? window.location.origin : '',
      invoiceId,
    );
    await updateDoc(invoiceRef, { paymentLink });

    for (const id of rsvpIds) {
      await updateDoc(doc(db, 'units', unitId, 'events', eventId, 'rsvps', id), {
        invoiceId,
        updatedAt: Timestamp.now(),
      });
    }

    const after = await getDoc(invoiceRef);
    if (!after.exists()) {
      return { ok: false, message: 'Invoice was created but could not be read back.' };
    }
    return {
      ok: true,
      invoice: mapInvoiceDoc(after.id, after.data() as Record<string, unknown>),
      created: true,
    };
  } catch (e) {
    console.error('ensureInvoiceForBooking', e);
    const msg = e instanceof Error ? e.message : 'Failed to create invoice.';
    return { ok: false, message: msg };
  }
}

/** Map a Firestore invoice document to the client `Invoice` shape (shared with attendance / dashboard). */
export function mapFirestoreInvoiceData(id: string, d: Record<string, unknown>): Invoice {
  return mapInvoiceDoc(id, d);
}
